import { httpClient } from './http-client';

/**
 * Performance API client.
 *
 * Return figures arrive as decimal STRINGS ("0.05" = +5%) because the backend
 * serializes shopspring decimals as quoted numbers; parse only at render time
 * so precision is never lost in transit.
 */

/** How a return figure was produced. The badge on the page renders this. */
export type PerformanceMethod = 'time_weighted' | 'value_return' | 'not_applicable';

export interface PerformanceSeriesPoint {
  date: string; // YYYY-MM-DD
  /** Flow-neutral growth since window start: "0.05" means +5%. */
  cumulative_return: string;
}

export interface AccountPerformanceRow {
  account_id: string;
  name: string;
  method: PerformanceMethod | string;
  return: string;
  annualized?: string;
  xirr?: string;
}

export interface BenchmarkSeries {
  symbol: string;
  return: string;
  series: PerformanceSeriesPoint[];
}

export interface PerformanceResponse {
  method: PerformanceMethod | string;
  return: string;
  annualized?: string;
  xirr?: string;
  window: { from: string; to: string };
  /** Share of window days every holding could be priced on (0..1). */
  price_coverage: number;
  /** Net external cash the ledger saw cross the scope boundary, base currency. */
  ledger_external_flows: string;
  series: PerformanceSeriesPoint[];
  accounts: AccountPerformanceRow[];
  benchmarks?: BenchmarkSeries[];
}

/** Benchmarks the compare endpoint accepts, offered in the picker. */
export const SUPPORTED_BENCHMARKS = ['SPY', 'XEQT.TO', 'VTI', 'VEQT.TO'] as const;

function windowQuery(from?: string, to?: string): string {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export class PerformanceService {
  static async getPortfolioPerformance(from?: string, to?: string): Promise<PerformanceResponse> {
    return httpClient.get<PerformanceResponse>(`/performance/portfolio${windowQuery(from, to)}`);
  }

  static async getAccountPerformance(
    accountId: string,
    from?: string,
    to?: string,
  ): Promise<PerformanceResponse> {
    return httpClient.get<PerformanceResponse>(
      `/performance/accounts/${accountId}${windowQuery(from, to)}`,
    );
  }

  static async compare(opts: {
    accounts?: string[];
    benchmarks?: string[];
    from?: string;
    to?: string;
  }): Promise<PerformanceResponse> {
    const params = new URLSearchParams();
    if (opts.accounts?.length) params.set('accounts', opts.accounts.join(','));
    if (opts.benchmarks?.length) params.set('benchmarks', opts.benchmarks.join(','));
    if (opts.from) params.set('from', opts.from);
    if (opts.to) params.set('to', opts.to);
    const qs = params.toString();
    return httpClient.get<PerformanceResponse>(`/performance/compare${qs ? `?${qs}` : ''}`);
  }
}
